import { hasDirectLink } from "@escapio/portal/state/DirectLink";
import { hasEscapioRate } from "@escapio/portal/state/escapio-deals-helpers";
import { getExperimentVersion } from "@escapio/portal/experiments/info";

export const BY_POPULARITY = "popular";
export const CHRONOLOGICAL = "chronological";
export const BY_PRICE_AND_POPULARITY = "price";
export const BY_PRICE_AND_CHRONOLOGICAL = "price_and_chronological";
export const BY_SCORE_AND_POPULARITY = "score";
export const BY_SCORE_AND_CHRONOLOGICAL = "score_and_chronological";

export const createSorter = (getter, ascending) => {
	return (prices, hasDates) => (a, b) => {
		const getterA = getter(a, prices, hasDates);
		const getterB = getter(b, prices, hasDates);

		if (getterA === getterB) {
			return 0;
		}
		return getterA < getterB != ascending ? 1 : -1;
	};
};

export const createBooleanSorter = getter => {
	return createSorter(
		(hotel, prices, hasDates) => !!getter(hotel, prices, hasDates),
		false,
	);
};

export const compose =
	(...sorters) =>
	(prices, hasDates) =>
	(a, b) =>
		sorters.reduce(
			// pass objects to next sorter until they are ordered.
			(compareResult, sorter) =>
				compareResult !== 0 ? compareResult : sorter(prices, hasDates)(a, b),
			0,
		);

export const hasPrice = (hotel, prices) => prices[hotel.id]?.hasOffers;

const groupByPriceState = hasDisabledPricesBetweenAvailablePrices =>
	createSorter((hotel, prices, hasDates) => {
		if (
			!hasDates ||
			hasPrice(hotel, prices) ||
			(hasDisabledPricesBetweenAvailablePrices && hotel.disabled_prices)
		) {
			return 0;
		}

		if (prices[hotel.id]?.isLoading) {
			return 1;
		}

		if (hasDirectLink(hotel)) {
			return 2;
		}

		return 3;
	}, true);

const byRanking = () => (a, b) => {
	if (a.ranking === b.ranking) {
		return 0;
	}

	return a.ranking < b.ranking ? 1 : -1;
};

const byActiveEscapioRate = createBooleanSorter(hotel => hasEscapioRate(hotel));

const byPrice = createSorter(
	(hotel, prices) => prices[hotel.id]?.bestPrice || Infinity,
	true,
);
const byScore = createSorter(hotel => hotel.review_score, false);
const chronological = createSorter(hotel => hotel.state.timeOrder, true);

const byContractBoost = () => (a, b) => {
	const isV1 = getExperimentVersion("exp29") === "v1";
	if (!isV1 || a.exp29_boost === b.exp29_boost) {
		return 0;
	}
	return a.exp29_boost ? -1 : 1;
};

const sorters = Object.freeze({
	[BY_POPULARITY]: compose(groupByPriceState(true), byContractBoost, byRanking),
	[CHRONOLOGICAL]: chronological,
	[BY_PRICE_AND_POPULARITY]: compose(
		groupByPriceState(false),
		byPrice,
		byActiveEscapioRate,
		byRanking,
	),
	[BY_PRICE_AND_CHRONOLOGICAL]: compose(
		groupByPriceState(false),
		byPrice,
		byActiveEscapioRate,
		chronological,
	),
	[BY_SCORE_AND_POPULARITY]: compose(
		byScore,
		groupByPriceState(true),
		byRanking,
	),
	[BY_SCORE_AND_CHRONOLOGICAL]: compose(
		byScore,
		groupByPriceState(true),
		chronological,
	),
});

export const isValidSorting = sorting => sorters.hasOwnProperty(sorting);
export const getSortingFunction = sorting => sorters[sorting];
